import NotificationEntity from "../Entities/NotificationItem";
import SAPOMailConfig     from '@/SAPOMailConfig';

/**
* Handles All Notifications
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @modifiedBy  rsommerfeld <roni.s.sommerfeld@altice.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
export default class Notifications {
  /**
   * Notification items zones
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  zones = {};

  /**
   * Count by zones
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  zones_count = {};

  /**
   * Default zone if options.zones not exists
  *
  * @private
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  ZONES_DEFAULT = ['float'];

  /**
   * Adds a messages from an object with type => message
   *
   * @since 7.0.0
   *
   * @param {Object} message      onject with type => message to be displayed
   * @param {Array}  replacements replacement to the message
   * @param {Object} options      {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
  addMessage(prefix, message, replacements, options = {}) {
    let messages_type = {
      'info'    : 'addInfo',
      'success' : 'addSuccess',
      'warning' : 'addWarning',
      'error'   : 'addError',
    };

    for (let type in message) {
      if (!messages_type[type]) {
        return;
      }

      this[messages_type[type]](prefix + message[type], replacements, options);
    }
  }

  /**
   * Adds a success notification
   *
   * @since 7.0.0
   *
   * @param {string} message      notification message
   * @param {Array}  replacements replacement to the message
   * @param {Object} options {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
  addSuccess(message, replacements, options = {}) {
    this.addNotification('success', message, replacements, options);
  }

  /**
   * Adds an error notification
   *
   * @since 7.0.0
   *
   * @param {string} message      notification message
   * @param {Array}  replacements replacement to the message
   * @param {Object} options      {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
  addError(message, replacements, options = {}) {
    this.addNotification('error', message, replacements, options);
  }

  /**
   * Adds a warning notification
   *
   * @since 7.0.0
   *
   * @param {string} message     notification message
   * @param {Array}  replacements replacement to the message
   * @param {Object} options {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
   addWarning(message, replacements, options = {}) {
    this.addNotification('warning', message, replacements, options);
  }

  /**
   * Adds a info notification
   *
   * @since 7.0.0
   *
   * @param {string} message     notification message
   * @param {Array}  replacements replacement to the message
   * @param {Object} options {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
    addInfo(message, replacements, options = {}) {
      this.addNotification('info', message, replacements, options);
    }

  /**
   * Adds a notification to the poll
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {string} type         notification type
   * @param {string} message      notification message
   * @param {Array}  replacements replacement to the message
   * @param {Object} options      {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {void}
   */
  addNotification(type, message, replacements, options = {}) {
    const zones = Array.isArray(options.zones) ? options.zones : this.ZONES_DEFAULT;

    delete options.zones;

    zones.forEach(zone => {
      const notification = this.mapNotificationItem(type, message, replacements, zone, options);

      if (!notification.allow_duplicates) {
        this.removeMessageDuplicate(zone, notification);
      }

      this.zones[zone] = this.zones[zone] || {};
      this.zones[zone][notification.id] = notification;

      if (notification.auto_dismiss) {
        this.startTimer(notification.id, zone);
      }
    });
  }

  /**
  * if a message already exists in a given zone
  * remove the ID of the message if it already exists
  *
  * @param {String}             zone         The zone to check for duplicate messages
  * @param {NotificationEntity} notification The notification to check for duplication
  *
  * @returns {void}
  */
  removeMessageDuplicate(zone, notification) {
    const messages_in_zone = this.zones[zone] ? Object.values(this.zones[zone]) : [];
    const duplicate        = messages_in_zone.find(item => item.message === notification.message);

    if (duplicate) {
      this.removeNotification(duplicate.id, zone)
    }
  }

  /**
   * Map items in NotificationEntity
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {string} type         notification type
   * @param {string} message      notification message
   * @param {Array}  replacements replacement to the message
   * @param {String} zone         The zone to check for duplicate messages
   * @param {Object} options      {zones, allow_duplicates, message_type, link, link_text, link_icon, auto_dismiss, link_class}
   *
   * @return {NotificationEntity}
   */
  mapNotificationItem(type, message, replacements, zone, options){
    this.zones_count[zone] = (this.zones_count[zone] || 0) + 1;

    let notification            = new NotificationEntity;
    notification.type           = type;
    notification.message        = message;
    notification.id             = `${notification.type}_${this.zones_count[zone]}`;
    notification.replacements   = replacements;
    notification                = Object.assign(notification, options);

    return notification;
  }

  /**
   * Starts the timer to auto remove the message notification
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} id   notification id
   * @param {String} zone The zone to check for duplicate messages
   *
   * @return {void}
   */
  startTimer(id, zone) {
    let object = this;

    setTimeout(() => {
      object.removeNotification(id, zone);
    }, SAPOMailConfig.client.notifications.timeout * 1000)
  }

  /**
   * Remove a notification from the poll
   *
   * @param {String} id notification id
   *
   * @public
   *
   * @since 7.0.0
   */
  removeNotification(id, zone) {
    if (!this.zones[zone] || !this.zones[zone].hasOwnProperty(id)) {
      return;
    }

    delete this.zones[zone][id];
  }
}